import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useProfiles } from '../../contexts/ProfileContext';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAuth } from '@/contexts/AuthContext';
import { priceFormatterByLocale } from '@/lib/utils';
import {
  ActiveSinceEnum,
  PeriodEnum,
  SpendingEnum,
} from '@/services/CrmService';

const formSchema = z.object({
  period: z.nativeEnum(PeriodEnum).optional(),
  spending: z.nativeEnum(SpendingEnum).optional(),
  activeSince: z.nativeEnum(ActiveSinceEnum).optional(),
});

type FormData = z.infer<typeof formSchema>;

export function SearchFilters() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Profiles.components.SearchFilters',
  });

  const { user } = useAuth();

  const { filter, resetFilters } = useProfiles();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  function handleClearFilters() {
    resetFilters();
    reset();
  }

  const onSubmit = handleSubmit((data) => {
    filter({
      period: data.period,
      spending: data.spending,
      activeSince: data.activeSince,
    });
  });

  return (
    <div>
      <form className="flex items-center gap-3" onSubmit={onSubmit}>
        <FormGroup error={{ message: errors.period?.message, font: 'sm' }}>
          <Controller
            control={control}
            name="period"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder={t('period.placeholder')} />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={PeriodEnum.LAST_7_DAYS}>
                    {t('period.last7Days')}
                  </SelectItem>

                  <SelectItem value={PeriodEnum.LAST_30_DAYS}>
                    {t('period.last30Days')}
                  </SelectItem>

                  <SelectItem value={PeriodEnum.LAST_3_MONTHS}>
                    {t('period.last3Months')}
                  </SelectItem>

                  <SelectItem value={PeriodEnum.LAST_6_MONTHS}>
                    {t('period.last6Months')}
                  </SelectItem>

                  <SelectItem value={PeriodEnum.LAST_12_MONTHS}>
                    {t('period.last12Months')}
                  </SelectItem>
                  <SelectItem value={PeriodEnum.MORE_THAN_12_MONTHS}>
                    {t('period.moreThan12Months')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormGroup>

        <FormGroup error={{ message: errors.spending?.message, font: 'sm' }}>
          <Controller
            control={control}
            name="spending"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder={t('spending.placeholder')} />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={SpendingEnum.UP_TO_500}>
                    {t('spending.upTo', {
                      value: priceFormatterByLocale(
                        500,
                        user!.customer.language,
                      ),
                    })}
                  </SelectItem>

                  <SelectItem value={SpendingEnum.FROM_500_TO_1000}>
                    {priceFormatterByLocale(500, user!.customer.language)} -{' '}
                    {priceFormatterByLocale(1000, user!.customer.language)}
                  </SelectItem>

                  <SelectItem value={SpendingEnum.FROM_1000_TO_2500}>
                    {priceFormatterByLocale(1000, user!.customer.language)} -{' '}
                    {priceFormatterByLocale(2500, user!.customer.language)}
                  </SelectItem>

                  <SelectItem value={SpendingEnum.FROM_2500_TO_5000}>
                    {priceFormatterByLocale(2500, user!.customer.language)} -{' '}
                    {priceFormatterByLocale(5000, user!.customer.language)}
                  </SelectItem>

                  <SelectItem value={SpendingEnum.ABOVE_5000}>
                    {t('spending.rangeAbove', {
                      value: priceFormatterByLocale(
                        5000,
                        user!.customer.language,
                      ),
                    })}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormGroup>
        <FormGroup error={{ message: errors.activeSince?.message, font: 'sm' }}>
          <Controller
            control={control}
            name="activeSince"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder={t('activeSince.placeholder')} />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={ActiveSinceEnum.LESS_THAN_3_MONTHS}>
                    {t('activeSince.lessThan3Months')}
                  </SelectItem>

                  <SelectItem value={ActiveSinceEnum.FROM_3_TO_6_MONTHS}>
                    {t('activeSince.3To6Months')}
                  </SelectItem>

                  <SelectItem value={ActiveSinceEnum.FROM_6_TO_12_MONTHS}>
                    {t('activeSince.6To12Months')}
                  </SelectItem>

                  <SelectItem value={ActiveSinceEnum.FROM_1_TO_2_YEARS}>
                    {t('activeSince.1To2Years')}
                  </SelectItem>

                  <SelectItem value={ActiveSinceEnum.MORE_THAN_2_YEARS}>
                    {t('activeSince.moreThan2Years')}
                  </SelectItem>

                  <SelectItem value={ActiveSinceEnum.NEW_CUSTOMERS}>
                    {t('activeSince.newCustomers')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormGroup>

        <Button type="submit" disabled={!isValid}>
          {t('applyFiltersButton')}
        </Button>

        <Button type="button" variant="outline" onClick={handleClearFilters}>
          {t('clearFiltersButton')}
        </Button>
      </form>
    </div>
  );
}
